/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React from 'react';

import { DeviceModel } from '../../../../core/device';
import { BrandingUiInfoKeys } from '../../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../../core/interactors';
import { DateTime } from '../../../../core/utils';
import { RECORDING_TYPE, RecordingStatusIcon } from '../../../Guide/RecordingStatusIcon';
import { ImageLoader } from '../../../ImageLoader';
import { ImageLoaderTypes } from '../../../ImageLoader/ImageLoaderTypes';
import { ShowInfoOneLineGuideProps } from './ShowInfoOneLineGuideProps';

import './ShowInfoOneLineGuide.scss';

const ShowInfoOneLineGuide: React.FC<ShowInfoOneLineGuideProps> = ({
    duration,
    isFutureMode,
    isNew,
    recordingStatus,
    isLive,
    providersImageUrls,
    startTime,
}) => {
    const endTime = startTime?.addSeconds(duration);
    const services = new ServicesClientImpl();
    const liveIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.LIVE_ICON);
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);

    const airingTime =
        isFutureMode && startTime.isToday()
            ? `${startTime.to12HourClock()} – ${endTime.to12HourClock()}`
            : DateTime.createDayDateTimeRange({
                  startTime: startTime?.getTime(),
                  duration,
                  lang: DeviceModel.getTVProperties().getLang(),
              });

    return (
        <div className="olg-show-info-container" data-testid="olg-show-info">
            <div className="olg-show-info-top-row">
                {isLive && (
                    <img
                        src={liveIcon?.imageUrl}
                        className={`olg-show-info-is-live ${liveIcon?.imageType}`}
                        data-testid="olg-preview-live-icon"
                    />
                )}
                <span className="olg-show-info-time">{airingTime}</span>
            </div>
            <div className="olg-show-info-bottom-row">
                <span className="olg-show-info-providers">
                    {recordingStatus === RECORDING_TYPE.GuidePreviewCompleteRecording ? (
                        <RecordingStatusIcon
                            type={RECORDING_TYPE.GuidePreviewCompleteRecording}
                            styleClass={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                        />
                    ) : null}
                    {providersImageUrls?.map((url: string, index: number) => (
                        <ImageLoader
                            key={index}
                            src={url}
                            imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                        />
                    ))}
                </span>
                {isNew && (
                    <img
                        src={newIcon?.imageUrl}
                        className={`olg-show-info-is-new ${newIcon?.imageType}`}
                        data-testid="olg-preview-new-icon"
                    />
                )}
            </div>
        </div>
    );
};

export default ShowInfoOneLineGuide;
